/**
 * HybridFileHandlerConfig.tsx
 * Properties-panel config for the HYBRID_FILE_HANDLER node.
 *
 * Follows exactly the same pattern as FileListenerConfig:
 *  1. "Source Type" dropdown (same shadcn Select)
 *  2. IntegrationConnectionUI for platform-managed credentials
 *  3. Source-specific path/location fields
 *  4. Relay Agent selector (same as RelaySourceConfig)
 *  5. PD column mapper
 *  6. Ready summary
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiGet, apiPost, apiFetch, interactiveFetch } from '@/api';
import { toast } from 'sonner';
import { IntegrationConnectionUI } from './IntegrationConnectionUI';
import {
  Server, ShieldAlert, AlertCircle, CheckCircle2, Clock,
  Plus, Trash2, RefreshCw, Lock, Loader2, Upload, FolderOpen, Info,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface RelayAgent { id: string; name: string; status: string; version?: string; heartbeat_at?: string; }
interface PdMapping {
  action: 'mask' | 'suppress' | 'hash' | 'date_year_only' | 'date_year_month' | 'date_month_only' | 'date_quarter' | 'date_age_range' | 'date_shift' | 'passthrough';
  mask_char?: string;
  mask_mode?: string;
  auto?: boolean;
}

interface HybridFileHandlerConfigProps {
  node: any;
  onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

// ─── Source types ─────────────────────────────────────────────────────────────
// "protocol" must match IntegrationConnectionUI's SERVICE_MAP keys for non-upload sources.
const SOURCE_TYPES = [
  { value: 'platform_upload', label: 'File Upload',          protocol: null          },
  { value: 'aws_s3',          label: 'AWS S3',              protocol: 's3'          },
  { value: 'azure_blob',      label: 'Azure Blob Storage',  protocol: 'azure'       },
  { value: 'sharepoint',      label: 'Microsoft SharePoint',protocol: 'sharepoint'  },
  { value: 'google_drive',    label: 'Google Drive',        protocol: 'gdrive'      },
  { value: 'onedrive',        label: 'Microsoft OneDrive',  protocol: 'onedrive'    },
];

const SOURCE_PLACEHOLDERS: Record<string, string> = {
  aws_s3:       's3://bucket-name/folder/file.csv',
  azure_blob:   'azure://container-name/path/file.csv',
  sharepoint:   '/FolderName/file.csv',
  google_drive: '1ABCxyz... (file ID from Google Drive)',
  onedrive:     '/Documents/folder/file.csv',
};

const PD_BASE_ACTIONS = [
  { value: 'mask',            label: 'Mask' },
  { value: 'suppress',        label: 'Suppress (drop)' },
  { value: 'hash',            label: 'Hash (SHA-256)' },
  { value: 'passthrough',     label: 'Passthrough' },
  { value: 'date_year_only',  label: 'Date → Year only' },
  { value: 'date_year_month', label: 'Date → Year-Month' },
  { value: 'date_month_only', label: 'Date → Month name' },
  { value: 'date_quarter',    label: 'Date → Quarter' },
  { value: 'date_age_range',  label: 'Date → Age range (5yr)' },
  { value: 'date_shift',      label: 'Date → ±30-day shift' },
];

const MASK_CHARS = [
  { value: '*',           label: '* (asterisk)' },
  { value: '#',           label: '# (hash)' },
  { value: 'X',           label: 'X (letter X)' },
  { value: '[REDACTED]',  label: '[REDACTED]' },
];

const MASK_MODES = [
  { value: 'full',            label: 'Full mask' },
  { value: 'partial_start',   label: 'Partial (keep start)' },
  { value: 'partial_end',     label: 'Partial (keep end)' },
  { value: 'format_preserve', label: 'Format-preserve' },
];

const DATE_COL_PATTERN = /\b(date|dob|birth|hired|joined|created|updated|timestamp|start|end|expire|issued)\b/i;

const STATUS_STYLES: Record<string, { variant: any; icon: any }> = {
  ACTIVE:  { variant: 'default',     icon: CheckCircle2 },
  IDLE:    { variant: 'secondary',   icon: Clock },
  PENDING: { variant: 'outline',     icon: Clock },
  REVOKED: { variant: 'destructive', icon: AlertCircle },
};

// ─── Main component ───────────────────────────────────────────────────────────
export function HybridFileHandlerConfig({ node, onChangeData }: HybridFileHandlerConfigProps) {
  const data = node?.data || {};

  const [agents,       setAgents]       = useState<RelayAgent[]>([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError,   setAgentError]   = useState<string | null>(null);
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState('');
  const [fetchingCols, setFetchingCols] = useState(false);
  const [fetchError,   setFetchError]   = useState('');
  const [columns,      setColumns]      = useState<string[]>([]);
  const [newCol,       setNewCol]       = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pdMappings: Record<string, PdMapping> = data.pdMappings || {};
  const selectedAgent = agents.find(a => a.id === data.agentId);
  const sourceType    = data.sourceType || '';
  const sourceDef     = SOURCE_TYPES.find(s => s.value === sourceType);
  const pdCount       = Object.keys(pdMappings).length;

  // ── Load relay agents ──────────────────────────────────────────────────────
  useEffect(() => {
    setAgentLoading(true);
    apiGet('/relay/agents')
      .then((res: any) => Array.isArray(res) && setAgents(res.filter((a: RelayAgent) => a.status !== 'REVOKED')))
      .catch(() => setAgentError('Could not load hybrid agents.'))
      .finally(() => setAgentLoading(false));
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const set  = (key: string, val: any) => onChangeData(key, val);
  const setPd = (col: string, mapping: PdMapping) =>
    set('pdMappings', { ...pdMappings, [col]: mapping });
  const removePd = (col: string) => {
    const next = { ...pdMappings }; delete next[col];
    set('pdMappings', next);
  };

  // ── Platform upload ────────────────────────────────────────────────────────
  const handleFileUpload = async (file: File) => {
    setUploading(true); setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      // apiFetch already parses JSON — returns the object directly (not a Response)
      const info = await apiFetch('/datasources/upload-temp', null, { method: 'POST', body: form });
      if (!info?.rawS3Path) throw new Error('Upload succeeded but S3 path was missing in response');
      onChangeData({ rawS3Path: info.rawS3Path, connectorConfig: { rawS3Path: info.rawS3Path, fileName: info.fileName }, path: info.rawS3Path });
      await fetchColumns('platform_upload', { rawS3Path: info.rawS3Path });
    } catch (e: any) {
      setUploadError(`Upload failed: ${e.message}`);
    } finally { setUploading(false); }
  };

  // ── Fetch columns ──────────────────────────────────────────────────────────
  const fetchColumns = useCallback(async (st: string, cfg: Record<string, any>) => {
    setFetchingCols(true);
    setFetchError('');
    try {
      const res  = await interactiveFetch('/preview-remote', null, {
        method: 'POST',
        body: JSON.stringify({
          sourceType: st,
          connectionId: data.connectionId || '',
          connectorConfig: { 
            ...cfg, 
            path: data.path || '',
            ...(data.connectionId === 'new' ? (data._newConnFields || {}) : {})
          },
        })
      });
      console.log('[fetchColumns] interactiveFetch response:', res);
      
      if (res?.error) {
        throw new Error(res.error);
      }
      
      const cols: string[] = res?.columns || [];
      setColumns(cols);
      if (cols.length) {
        type AutoRule = [RegExp, PdMapping['action'], Partial<PdMapping>?];
        const AUTO: AutoRule[] = [
          [/\b(email|mail)\b/i,          'mask',           { mask_char: '*', mask_mode: 'partial_start' }],
          [/\b(phone|mobile|cell)\b/i,   'mask',           { mask_char: '*', mask_mode: 'partial_end' }],
          [/\b(ssn|tax_id|national_id)\b/i, 'suppress',   {}],
          [/\b(password|secret|token)\b/i,  'suppress',   {}],
          [/\b(dob|date_of_birth)\b/i,   'date_age_range', {}],
          [/\b(hire_date|start_date|join)\b/i, 'date_year_only', {}],
          [/\b(birth.*date|date.*birth)\b/i, 'date_year_only', {}],
          [/\b(credit_card|cvv|pan)\b/i, 'suppress',       {}],
          [/\b(salary|income|wage)\b/i,  'mask',           { mask_char: '*', mask_mode: 'full' }],
          [/\b(address|postal)\b/i,      'mask',           { mask_char: '*', mask_mode: 'full' }],
        ];
        const auto: Record<string, PdMapping> = {};
        for (const col of cols)
          for (const [pat, action, extra] of AUTO)
            if (pat.test(col) && !pdMappings[col]) { auto[col] = { action, auto: true, ...extra }; break; }
        if (Object.keys(auto).length) set('pdMappings', { ...auto, ...pdMappings });
      }
    } catch (e: any) {
      const msg = `Failed to fetch columns: ${e.message || 'Unknown error'}`;
      setFetchError(msg);
      toast.error(msg);
    } finally { setFetchingCols(false); }
  }, [data, pdMappings]);

  const isReady = !!data.agentId && !!sourceType && (
    sourceType === 'platform_upload' ? !!data.rawS3Path : !!data.path
  );

  return (
    <div className="space-y-5">

      {/* ── 0. INFO PANEL ──────────────────────────────────────────────── */}
      <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-indigo-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-indigo-900">Hybrid File Handler</p>
            <p className="text-[11px] text-indigo-700 mt-1 leading-relaxed">
              Securely ingests files from cloud or on-premise sources via a Hybrid Agent.
              Sensitive columns are automatically detected and sanitized before data reaches the platform.
            </p>
            <details className="mt-2">
              <summary className="text-[10px] text-indigo-500 cursor-pointer hover:text-indigo-700 font-medium">How it works →</summary>
              <ol className="text-[10px] text-indigo-600 mt-1.5 space-y-1 pl-4 list-decimal">
                <li>Agent downloads the file using your credentials (never leaves your network)</li>
                <li>Parses CSV / Excel / JSON / Parquet into a data frame</li>
                <li>Auto-detects PII/PHI columns (email, SSN, salary, etc.) and applies sanitization</li>
                <li>Uploads the cleaned Parquet to platform storage via a time-limited presigned URL</li>
                <li>Raw source file is <strong>never</strong> stored on the platform — only the sanitized output</li>
              </ol>
            </details>
          </div>
        </div>
      </div>

      {/* ── 1. HYBRID AGENT ────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
          <Server className="h-3.5 w-3.5 text-purple-500" /> Hybrid Agent
        </label>

        {agentError && (
          <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />{agentError}
          </div>
        )}

        <Select
          value={data.agentId || ''}
          onValueChange={(id: string) => {
            const agent = agents.find(a => a.id === id);
            onChangeData({ agentId: id, agentName: agent?.name || '' });
          }}
          disabled={agentLoading || agents.length === 0}
        >
          <SelectTrigger className="w-full text-sm">
            <SelectValue placeholder={
              agentLoading ? 'Loading agents…'
              : agents.length === 0 ? 'No active agents found'
              : 'Select a Hybrid Agent…'
            } />
          </SelectTrigger>
          <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
            {agents.map(agent => {
              const style = STATUS_STYLES[agent.status] || STATUS_STYLES['IDLE'];
              const Icon  = style.icon;
              return (
                <SelectItem key={agent.id} value={agent.id}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-3 w-3 text-gray-400 shrink-0" />
                    <span>{agent.name}</span>
                    <Badge variant={style.variant} className="text-[9px] px-1 py-0 ml-auto">{agent.status}</Badge>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {selectedAgent && (
          <div className="p-2 bg-purple-50 border border-purple-100 rounded-lg flex items-center justify-between">
            <span className="text-[11px] font-medium text-purple-900">{selectedAgent.name}</span>
            <Badge variant={STATUS_STYLES[selectedAgent.status]?.variant || 'secondary'} className="text-[9px]">
              {selectedAgent.status}
            </Badge>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100" />

      {/* ── 2. SOURCE TYPE (dropdown — matches File Listener style) ──── */}
      <div className="space-y-2">
        <Label>Source Type</Label>
        <Select
          value={sourceType}
          onValueChange={(v) => onChangeData({ sourceType: v, path: '', connectionId: '', rawS3Path: '', pdMappings: {} })}
        >
          <SelectTrigger className="w-full bg-white border-gray-300">
            <SelectValue placeholder="Select source type" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-300 shadow-lg z-[100]">
            <SelectItem value="platform_upload">File Upload</SelectItem>
            <SelectItem value="aws_s3">AWS S3</SelectItem>
            <SelectItem value="azure_blob">Azure Blob Storage</SelectItem>
            <SelectItem value="sharepoint">Microsoft SharePoint</SelectItem>
            <SelectItem value="google_drive">Google Drive</SelectItem>
            <SelectItem value="onedrive">Microsoft OneDrive</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">Where your files are stored</p>
      </div>

      {/* ── 3. PLATFORM UPLOAD ──────────────────────────────────────── */}
      {sourceType === 'platform_upload' && (
        <div className="space-y-2">
          {data.rawS3Path ? (
            <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="text-[11px] font-semibold text-blue-900">{data.connectorConfig?.fileName || 'File uploaded'}</div>
              <div className="text-[10px] text-blue-500 mt-0.5">Stored in secure temp storage · deleted after processing</div>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors
                ${uploading ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFileUpload(f); }}
            >
              {uploading
                ? <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto mb-2" />
                : <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              }
              <div className="text-xs font-medium text-gray-700">
                {uploading ? 'Uploading…' : 'Drop file here or click to browse'}
              </div>
              <div className="text-[11px] text-gray-400 mt-1">CSV · TSV · TXT · Excel (.xlsx/.xls) · JSON · JSONL · Parquet</div>
              <input ref={fileInputRef} type="file" className="hidden"
                accept=".csv,.tsv,.txt,.xlsx,.xls,.json,.jsonl,.ndjson,.parquet"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
              />
            </div>
          )}
          {uploadError && <p className="text-[11px] text-red-600">{uploadError}</p>}
        </div>
      )}

      {/* ── 4. CREDENTIALS via IntegrationConnectionUI ──────────────── */}
      {/* Exactly the same way FileListenerConfig uses it */}
      {sourceType && sourceType !== 'platform_upload' && sourceDef?.protocol && (
        <IntegrationConnectionUI
          protocol={sourceDef.protocol}
          connectionId={data.connectionId || ''}
          onChangeConnectionId={(v) => set('connectionId', v)}
          newConnectionLabel={data._newConnLabel || ''}
          newConnectionFields={data._newConnFields || {}}
          onChangeNewLabel={(v) => set('_newConnLabel', v)}
          onChangeNewFields={(v) => set('_newConnFields', v)}
        />
      )}

      {/* ── 5. PATH / LOCATION for each source ──────────────────────── */}
      {sourceType && sourceType !== 'platform_upload' && (
        <div className="space-y-2">
          <Label>
            {sourceType === 'aws_s3'       && 'S3 Path'}
            {sourceType === 'azure_blob'   && 'Container / Blob Path'}
            {sourceType === 'sharepoint'   && 'SharePoint File Path'}
            {sourceType === 'google_drive' && 'File ID'}
            {sourceType === 'onedrive'     && 'OneDrive File Path'}
          </Label>

          {/* SharePoint needs site domain + site name */}
          {sourceType === 'sharepoint' && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="space-y-1">
                <Label className="text-[11px] text-gray-500">Domain</Label>
                <Input className="text-xs" value={data.sharepointDomain || ''} placeholder="contoso"
                  onChange={e => set('sharepointDomain', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-gray-500">Site Name</Label>
                <Input className="text-xs" value={data.siteName || ''} placeholder="TeamSite"
                  onChange={e => set('siteName', e.target.value)} />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              className="text-xs flex-1"
              value={data.path || ''}
              onChange={e => set('path', e.target.value)}
              placeholder={SOURCE_PLACEHOLDERS[sourceType] || 'Enter path'}
            />
            {fetchingCols && <Loader2 className="h-4 w-4 animate-spin text-gray-400 self-center" />}
          </div>

          <p className="text-[11px] text-gray-400">
            {sourceType === 'aws_s3'       && 'Format: s3://bucket-name/folder/file.csv'}
            {sourceType === 'azure_blob'   && 'Format: azure://container/path/file.csv'}
            {sourceType === 'sharepoint'   && 'Path relative to the document library root (e.g. /FolderName/file.csv)'}
            {sourceType === 'google_drive' && 'The file ID from the Google Drive URL (e.g. 1ABCxyz...)'}
            {sourceType === 'onedrive'     && 'Path relative to your OneDrive root (e.g. /Documents/file.csv)'}
          </p>

          {/* Fetch columns button */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              fetchColumns(sourceType, {
                path: data.path,
                rawS3Path: data.rawS3Path,
                // SharePoint-specific
                sharepointDomain: data.sharepointDomain, siteName: data.siteName,
              });
            }}
            disabled={fetchingCols || !data.path}
            className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium disabled:opacity-40 transition-colors"
          >
            <RefreshCw className={`h-3 w-3 ${fetchingCols ? 'animate-spin' : ''}`} />
            {fetchingCols ? 'Fetching columns…' : 'Fetch Columns'}
          </button>
          {fetchError && <p className="text-[11px] text-red-600 mt-2">{fetchError}</p>}
        </div>
      )}

      {/* ── 6. PD COLUMN MAPPER ─────────────────────────────────────── */}
      {(pdCount > 0 || columns.length > 0) && (
        <>
          <div className="border-t border-gray-100" />
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
              Protected Data Columns
              {pdCount > 0 && (
                <span className="ml-auto text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-normal">
                  {pdCount} mapped
                </span>
              )}
            </label>

            {Object.entries(pdMappings).map(([col, mapping]) => {
              const isMask = mapping.action === 'mask';
              const isDate = mapping.action.startsWith('date_');
              return (
                <div key={col} className="p-1.5 bg-amber-50 border border-amber-100 rounded-lg space-y-1">
                  {/* Row 1: col name + action selector + remove */}
                  <div className="flex items-center gap-2">
                    <Lock className="h-3 w-3 text-amber-500 shrink-0" />
                    <span className="flex-1 text-[11px] font-medium text-gray-800 truncate">{col}</span>
                    {mapping.auto && <span className="text-[9px] text-gray-400 bg-gray-100 px-1 rounded">auto</span>}
                    <select
                      value={mapping.action}
                      onChange={e => setPd(col, { action: e.target.value as any, auto: mapping.auto })}
                      className="text-[11px] border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-700 max-w-[140px]"
                    >
                      {PD_BASE_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                    <button onClick={() => removePd(col)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  {/* Row 2: mask sub-options (only for mask action) */}
                  {isMask && (
                    <div className="flex items-center gap-1.5 pl-5">
                      <span className="text-[10px] text-gray-400">Char:</span>
                      <select
                        value={mapping.mask_char || '*'}
                        onChange={e => setPd(col, { ...mapping, mask_char: e.target.value })}
                        className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-600"
                      >
                        {MASK_CHARS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      <span className="text-[10px] text-gray-400">Mode:</span>
                      <select
                        value={mapping.mask_mode || 'full'}
                        onChange={e => setPd(col, { ...mapping, mask_mode: e.target.value })}
                        className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-600 max-w-[110px]"
                      >
                        {MASK_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex gap-1.5">
              <Input
                className="flex-1 text-xs"
                placeholder="Add column name…"
                value={newCol}
                onChange={e => setNewCol(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newCol.trim()) { setPd(newCol.trim(), { action: 'mask' }); setNewCol(''); } }}
              />
              <Button
                size="sm"
                onClick={() => { if (newCol.trim()) { setPd(newCol.trim(), { action: 'mask' }); setNewCol(''); } }}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8"
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>

            {columns.filter(c => !pdMappings[c]).length > 0 && (
              <details>
                <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-700">
                  {columns.filter(c => !pdMappings[c]).length} detected columns (click to mark as PD)
                </summary>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {columns.filter(c => !pdMappings[c]).map(col => (
                    <button key={col} onClick={() => setPd(col, { action: 'mask' })}
                      className="text-[10px] px-2 py-0.5 rounded border border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-700">
                      + {col}
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>
        </>
      )}

      {/* ── 7. READY SUMMARY ────────────────────────────────────────── */}
      {isReady && (
        <>
          <div className="border-t border-gray-100" />
          <div className="p-2.5 bg-green-50 border border-green-100 rounded-lg space-y-1">
            <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wider">✅ Ready to Run</p>
            <div className="text-[10px] text-gray-600 space-y-0.5">
              <div>Agent: <span className="font-medium text-gray-900">{data.agentName || data.agentId}</span></div>
              <div>Source: <span className="font-medium text-gray-900">
                {SOURCE_TYPES.find(s => s.value === sourceType)?.label || sourceType}
              </span></div>
              {pdCount > 0 && (
                <div className="flex items-center gap-1 text-amber-600">
                  <ShieldAlert className="h-3 w-3" />
                  {pdCount} PD column{pdCount !== 1 ? 's' : ''} will be sanitized
                </div>
              )}
              {sourceType === 'platform_upload' && (
                <div className="flex items-center gap-1 text-red-600 text-[9px]">
                  <Lock className="h-2.5 w-2.5" /> Raw file deleted immediately after processing
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HybridFileHandlerConfig;
